import { PayorType, type Claim, type Payor } from 'interfaces';
import { createElement } from 'react';
import { DateTime } from 'luxon';
import { isFeatureEnabled } from '../firebase';
import { CLAIM_TARIFF_STATUS } from '@billing/validation/tariff';
import {
  getClaimTariffStatuses,
  claimHasTariffStatus,
  isCancelledClaim,
  isReconcileEligibleTariffStatus,
} from './claimStatusUtils';
import { navigateToPath } from './miscUtils';
import {
  IconArrowsLeftRight,
  IconBan,
  IconBookUpload,
  IconCircleCheck,
  IconCircleMinus,
  IconCopy,
  IconCurrencyDollarOff,
  IconFileInvoice,
  IconHistory,
  IconLink,
  IconMessageCircle,
  IconQuestionMark,
  IconReceipt2,
  IconRepeatOnce,
  IconShieldOff,
  IconHistoryToggle,
  IconTrashX,
  IconCurrencyDollar,
  IconReceiptDollar,
  IconDirections,
  IconListCheck,
  IconExternalLink,
  IconDatabaseSearch,
  IconPaperclip,
  IconFileUpload,
} from '@tabler/icons-react';

// Action identifiers used in translation keys, ordering, and execution handlers.
export enum Action {
  CREATE_REQUEST = 'createRequest',
  VIEW_REQUESTS = 'viewRequests',
  INTERNAL_COMMENT = 'internalComment',
  OPEN_LINKED_TASK = 'openLinkedTask',
  VIEW_HISTORY = 'viewHistory',
  VIEW_DEBITS = 'viewDebits',
  VIEW_SUBMISSIONS = 'viewSubmissions',
  VIEW_QUERIES = 'viewQueries',
  VIEW_BATCH = 'viewBatch',
  RESUBMIT = 'resubmit', // legacy claim-row action; intentionally not accessible
  INVOICE = 'invoice',
  QUERY = 'query',
  RECONCILE = 'reconcile',
  MARK_INCOMPLETE = 'markIncomplete', // legacy claim-row action; intentionally not accessible
  MARK_CANCELLED = 'markCancelled',
  MARK_PENDING_WRITEOFF = 'markPendingWriteOff',
  MARK_UNINSURED = 'markUninsured',
  MARK_HISTORICAL = 'markHistorical', // legacy claim-row action; intentionally not accessible
  MARK_READY = 'markReady', // legacy claim-row action; intentionally not accessible
  MARK_PAID = 'markPaid',
  WRITE_OFF = 'writeOff',
  MATCH = 'match',
  VIEW_INVOICE = 'viewInvoice',
  VIEW_RECEIPT = 'viewReceipt',
  DUPLICATE = 'duplicate',
  VIEW_SOURCE_DATA = 'viewSourceData',
  VIEW_ATTACHMENTS = 'viewAttachments',
  UPLOAD_SUPPORTING_DOCUMENT = 'uploadSupportingDocument',
}

export enum ActionMenuGroup {
  DEFAULT = 'default',
  MARK_AS_TRANSITIONS = 'markAsTransitions',
}

export interface ActionHandlerContext {
  claim: Claim;
  canModify: boolean;
  canCreateQuery?: boolean;
  setSelectedClaim?: (claim: Claim) => void;
  setHistoryOpen?: (open: boolean) => void;
  setDebitOpen?: (open: boolean) => void;
  setInvoiceOpen?: (open: boolean) => void;
  setSubmissionOpen?: (open: boolean) => void;
  setViewBatchOpen?: (open: boolean) => void;
  setCancelClaimOpen?: (open: boolean) => void;
  setQueryModalOpen?: (open: boolean) => void;
  setInvoiceModalOpen?: (open: boolean) => void;
  setResubmitOpen?: (open: boolean) => void;
  setReconcileOpen?: (open: boolean) => void;
  setMatchOpen?: (open: boolean) => void;
  setWriteOffOpen?: (open: boolean) => void;
  setMarkReadyOpen?: (open: boolean) => void;
  setMarkPaidOpen?: (open: boolean) => void;
  setMarkIncompleteOpen?: (open: boolean) => void;
  setMarkHistoricalOpen?: (open: boolean) => void;
  setMarkUninsuredOpen: (open: boolean) => void;
  setMarkPendingWriteOffOpen: (open: boolean) => void;
  setInternalCommentOpen: (open: boolean) => void;
  setReceiptOpen: (open: boolean) => void;
  onDuplicateClaim?: (claim: Claim) => void;
  onUnhandledAction?: (action: Action, context: ActionHandlerContext) => void;
  setActionRequestOpen: (open: boolean) => void;
  setViewActionsOpen: (open: boolean) => void;
  setSourceDataOpen?: (open: boolean) => void;
  setAttachmentsOpen?: (open: boolean) => void;
  setSupportingDocumentOpen?: (open: boolean) => void;
  onClaimReconcile?: (claim: Claim) => void;
}

export interface ActionMetadata {
  type: Action;
  menuGroup?: ActionMenuGroup;
  label: string;
  icon: React.ReactNode | null;
  execute: (context: ActionHandlerContext) => void;
  disabled?: (context: ActionHandlerContext) => boolean;
}

export type ActionMenuEntry =
  | { kind: 'action'; action: ActionMetadata }
  | { kind: 'group'; group: ActionMenuGroup; actions: ActionMetadata[] };

type ActionLabelTranslator = (key: string, defaultValue?: string) => string;

const ICON_SIZE = 18;

// Single canonical menu order for all non-provider users.
const ACTION_ORDER: Action[] = [
  Action.RECONCILE,
  Action.MATCH,
  Action.MARK_READY,
  Action.MARK_INCOMPLETE,
  Action.MARK_PAID,
  Action.MARK_PENDING_WRITEOFF,
  Action.WRITE_OFF,
  Action.MARK_UNINSURED,
  Action.MARK_CANCELLED,
  Action.INVOICE,
  Action.UPLOAD_SUPPORTING_DOCUMENT,
  Action.QUERY,
  Action.INTERNAL_COMMENT,
  Action.VIEW_HISTORY,
  Action.OPEN_LINKED_TASK,
  Action.VIEW_DEBITS,
  Action.VIEW_SUBMISSIONS,
  Action.VIEW_QUERIES,
  Action.VIEW_BATCH,
  Action.VIEW_INVOICE,
  Action.VIEW_RECEIPT,
  Action.VIEW_ATTACHMENTS,
  Action.VIEW_SOURCE_DATA,
  Action.CREATE_REQUEST,
  Action.VIEW_REQUESTS,
  Action.DUPLICATE,
];

const PROVIDER_ACTION_ORDER: Action[] = [
  Action.VIEW_HISTORY,
  Action.VIEW_DEBITS,
  Action.VIEW_SOURCE_DATA,
  Action.VIEW_REQUESTS,
];

const ADMIN_ACTION_ORDER: Action[] = [
  Action.VIEW_HISTORY,
  Action.VIEW_DEBITS,
  Action.VIEW_QUERIES,
  Action.VIEW_ATTACHMENTS,
  Action.VIEW_SOURCE_DATA,
];

// Actions that transition claim state and are grouped in a secondary menu.
export const STATUS_TRANSITION_ACTIONS = new Set<Action>([
  Action.MARK_PAID,
  Action.MARK_PENDING_WRITEOFF,
  Action.MARK_UNINSURED,
  Action.MARK_CANCELLED,
  Action.WRITE_OFF,
]);

const selectClaim = (context: ActionHandlerContext) => {
  context.setSelectedClaim?.(context.claim);
};

const hasClaimInvoices = (claim: Claim): boolean =>
  (claim?.invoices || []).some((invoice) =>
    Boolean(invoice?.id || invoice?.file)
  );

export const hasSCIClaimQueryData = (claim: Claim): boolean => {
  const hasProviderLicense = claim.provider?.licenses?.some(
    (license) =>
      license.siteID === claim.siteID &&
      license.payorID === claim.payorID &&
      Boolean(license.license?.trim())
  );
  const payorDebits = (claim.debits ?? []).filter(
    (debit) => debit.payorID === claim.payorID
  );
  const latestDebitByTariffID = new Map<string, (typeof payorDebits)[number]>();
  payorDebits.forEach((debit) => {
    const existing = latestDebitByTariffID.get(debit.tariffID);
    if (
      !existing ||
      DateTime.fromISO(debit.createdAt).toMillis() >
        DateTime.fromISO(existing.createdAt).toMillis() ||
      (debit.createdAt === existing.createdAt && debit.id > existing.id)
    ) {
      latestDebitByTariffID.set(debit.tariffID, debit);
    }
  });
  const latestDebits = [...latestDebitByTariffID.values()];
  const currentTariffIDs = new Set(
    (claim.tariffs ?? []).map((tariff) => tariff.id)
  );
  const hasMicrofilm = latestDebits.some(
    (debit) =>
      currentTariffIDs.has(debit.tariffID) && Boolean(debit.microfilm?.trim())
  );
  const hasDebitForEveryTariff = (claim.tariffs ?? []).every((tariff) =>
    latestDebits.some(
      (debit) => debit.tariffID === tariff.id && debit.requestedAmount != null
    )
  );

  return Boolean(
    claim.claimNumber?.trim() &&
      claim.site?.siteNumber?.trim() &&
      claim.healthcard?.number?.trim() &&
      (claim.healthcard?.province?.trim() || claim.patient?.province?.trim()) &&
      hasProviderLicense &&
      hasMicrofilm &&
      claim.tariffs?.length &&
      hasDebitForEveryTariff
  );
};

export const isSubmittedClaimPayorEditable = (
  payorType: Payor['type'] | undefined | null
): boolean => payorType === PayorType.PRIVATE || payorType === PayorType.DIRECT;

export const canUpdateSubmittedClaimStatus = (claim: Claim): boolean => {
  const payorType = claim?.payor?.type;
  return isSubmittedClaimPayorEditable(payorType);
};

const actionableTariffStatuses = new Set<string>([
  CLAIM_TARIFF_STATUS.INCOMPLETE,
  CLAIM_TARIFF_STATUS.READY,
  CLAIM_TARIFF_STATUS.SUBMITTED,
  CLAIM_TARIFF_STATUS.PENDING,
  CLAIM_TARIFF_STATUS.REJECTED,
  CLAIM_TARIFF_STATUS.UNDERPAID,
  CLAIM_TARIFF_STATUS.OVERPAID,
  CLAIM_TARIFF_STATUS.QUERIED,
  CLAIM_TARIFF_STATUS.UNINSURED,
  CLAIM_TARIFF_STATUS.PAYOR_INVOICED,
  CLAIM_TARIFF_STATUS.REVERTED,
  CLAIM_TARIFF_STATUS.PENDING_WRITE_OFF,
  CLAIM_TARIFF_STATUS.UNMATCHED,
  CLAIM_TARIFF_STATUS.HISTORICAL,
]);

const submittedRollupStatuses = new Set<string>([
  CLAIM_TARIFF_STATUS.SUBMITTED,
  CLAIM_TARIFF_STATUS.PENDING,
  CLAIM_TARIFF_STATUS.QUERIED,
  CLAIM_TARIFF_STATUS.PAYOR_INVOICED,
]);

const hasTariffStatus = (claim: Claim, statuses: Set<string>): boolean =>
  getClaimTariffStatuses(claim).some((status) => statuses.has(status));

const hasAnyActionableTariff = (claim: Claim): boolean =>
  hasTariffStatus(claim, actionableTariffStatuses);

const hasSubmissions = (claim: Claim): boolean =>
  Array.isArray(claim.submissions) && claim.submissions.length > 0;

const canDisplayAction = (
  action: Action,
  claim: Claim,
  canViewQueries = false
): boolean => {
  switch (action) {
    case Action.RECONCILE:
      return getClaimTariffStatuses(claim).some((status) =>
        isReconcileEligibleTariffStatus(status)
      );
    case Action.MATCH:
      return getClaimTariffStatuses(claim).includes(
        CLAIM_TARIFF_STATUS.UNMATCHED
      );
    case Action.MARK_PAID:
      return hasAnyActionableTariff(claim);
    case Action.MARK_PENDING_WRITEOFF:
      return hasAnyActionableTariff(claim);
    case Action.WRITE_OFF:
      return getClaimTariffStatuses(claim).includes(
        CLAIM_TARIFF_STATUS.PENDING_WRITE_OFF
      );
    case Action.MARK_UNINSURED:
      return hasAnyActionableTariff(claim);
    case Action.MARK_CANCELLED:
      return !isCancelledClaim(claim) && hasAnyActionableTariff(claim);
    case Action.MARK_INCOMPLETE:
    case Action.MARK_READY:
      return (claim.tariffs?.length ?? 0) > 0 && !isCancelledClaim(claim);
    case Action.INVOICE:
      return (
        !isCancelledClaim(claim) &&
        (claim?.payor?.type === PayorType.PRIVATE ||
          claim?.payor?.type === PayorType.DIRECT ||
          hasTariffStatus(claim, submittedRollupStatuses))
      );
    case Action.QUERY:
      return claim?.payor?.type === PayorType.PUBLIC;
    case Action.UPLOAD_SUPPORTING_DOCUMENT:
      return claim?.payor?.name === 'MB';
    case Action.INTERNAL_COMMENT:
    case Action.VIEW_HISTORY:
    case Action.VIEW_DEBITS:
    case Action.VIEW_SOURCE_DATA:
    case Action.VIEW_ATTACHMENTS:
    case Action.CREATE_REQUEST:
    case Action.VIEW_REQUESTS:
    case Action.DUPLICATE:
      return true;
    case Action.OPEN_LINKED_TASK:
      return Boolean(claim?.intakeItemID);
    case Action.VIEW_SUBMISSIONS:
      return hasSubmissions(claim);
    case Action.VIEW_QUERIES:
      return canViewQueries && (claim.claimQueryIDs?.length ?? 0) > 0;
    case Action.VIEW_BATCH:
      return true;
    case Action.VIEW_INVOICE:
      return hasClaimInvoices(claim);
    case Action.VIEW_RECEIPT:
      return hasClaimInvoices(claim);
    case Action.RESUBMIT:
    case Action.MARK_HISTORICAL:
      return false;
    default:
      return false;
  }
};

const getActionMenuGroup = (action: ActionMetadata): ActionMenuGroup =>
  action.menuGroup ?? ActionMenuGroup.DEFAULT;

const baseActionMetadata: Record<Action, ActionMetadata> = {
  [Action.INTERNAL_COMMENT]: {
    type: Action.INTERNAL_COMMENT,
    label: 'Internal Comment',
    icon: createElement(IconMessageCircle, { size: ICON_SIZE }),
    execute: (context) => {
      selectClaim(context);
      context.setInternalCommentOpen(true);
    },
    disabled: (context) => {
      return !context.canModify;
    },
  },
  [Action.VIEW_HISTORY]: {
    type: Action.VIEW_HISTORY,
    label: 'View History',
    icon: createElement(IconHistory, { size: ICON_SIZE }),
    execute: (context) => {
      selectClaim(context);
      context.setHistoryOpen(true);
    },
  },
  [Action.OPEN_LINKED_TASK]: {
    type: Action.OPEN_LINKED_TASK,
    label: 'Open Linked Upload',
    icon: createElement(IconExternalLink, { size: ICON_SIZE }),
    execute: (context) => {
      const taskID = context.claim?.intakeItemID;
      if (!taskID || typeof window === 'undefined') return;
      window.open(`/uploads/edit/${taskID}`, '_blank', 'noopener,noreferrer');
    },
    disabled: (context) => {
      return !context.claim?.intakeItemID;
    },
  },
  [Action.VIEW_DEBITS]: {
    type: Action.VIEW_DEBITS,
    label: 'View Payment History',
    icon: createElement(IconReceiptDollar, { size: ICON_SIZE }),
    execute: (context) => {
      selectClaim(context);
      context.setDebitOpen(true);
    },
  },
  [Action.VIEW_SUBMISSIONS]: {
    type: Action.VIEW_SUBMISSIONS,
    label: 'View Submissions',
    icon: createElement(IconBookUpload, { size: ICON_SIZE }),
    execute: (context) => {
      selectClaim(context);
      context.setSubmissionOpen(true);
    },
  },
  [Action.VIEW_QUERIES]: {
    type: Action.VIEW_QUERIES,
    label: 'View Queries',
    icon: createElement(IconExternalLink, { size: ICON_SIZE }),
    execute: (context) => {
      navigateToPath({ path: `/queries?claimID=${context.claim.id}` });
    },
  },
  [Action.VIEW_BATCH]: {
    type: Action.VIEW_BATCH,
    label: 'View Batch',
    icon: createElement(IconExternalLink, { size: ICON_SIZE }),
    execute: (context) => {
      selectClaim(context);
      context.setViewBatchOpen?.(true);
    },
    disabled: (context) => {
      return !context.claim?.batchID;
    },
  },
  [Action.RESUBMIT]: {
    type: Action.RESUBMIT,
    label: 'Resubmit',
    icon: createElement(IconRepeatOnce, { size: ICON_SIZE }),
    execute: (context) => {
      selectClaim(context);
      context.setResubmitOpen(true);
    },
    disabled: (context) => {
      return !context.canModify;
    },
  },
  [Action.INVOICE]: {
    type: Action.INVOICE,
    label: 'Invoice',
    icon: createElement(IconFileInvoice, { size: ICON_SIZE }),
    execute: (context) => {
      selectClaim(context);
      context.setInvoiceModalOpen(true);
    },
    disabled: (context) => {
      return !context.canModify;
    },
  },
  [Action.QUERY]: {
    type: Action.QUERY,
    label: 'Query',
    icon: createElement(IconQuestionMark, { size: ICON_SIZE }),
    execute: (context) => {
      selectClaim(context);
      context.setQueryModalOpen(true);
    },
    disabled: (context) => {
      return !context.canCreateQuery;
    },
  },
  [Action.RECONCILE]: {
    type: Action.RECONCILE,
    label: 'Reconcile',
    icon: createElement(IconArrowsLeftRight, { size: ICON_SIZE }),
    execute: (context) => {
      selectClaim(context);
      if (context.onClaimReconcile) {
        context.onClaimReconcile(context.claim);
        return;
      }
      context.setReconcileOpen(true);
    },
    disabled: (context) => {
      const debits = context.claim.debits || [];
      return debits.length === 0 || !context.canModify;
    },
  },
  [Action.MARK_INCOMPLETE]: {
    type: Action.MARK_INCOMPLETE,
    menuGroup: ActionMenuGroup.MARK_AS_TRANSITIONS,
    label: 'Mark Incomplete',
    icon: createElement(IconCircleMinus, { size: ICON_SIZE }),
    execute: (context) => {
      selectClaim(context);
      context.setMarkIncompleteOpen(true);
    },
    disabled: (context) => {
      return !context.canModify;
    },
  },
  [Action.MARK_PAID]: {
    type: Action.MARK_PAID,
    menuGroup: ActionMenuGroup.MARK_AS_TRANSITIONS,
    label: 'Mark Paid',
    icon: createElement(IconCurrencyDollar, { size: ICON_SIZE }),
    execute: (context) => {
      selectClaim(context);
      context.setMarkPaidOpen(true);
    },
    disabled: (context) => {
      return !context.canModify;
    },
  },
  [Action.MARK_CANCELLED]: {
    type: Action.MARK_CANCELLED,
    menuGroup: ActionMenuGroup.MARK_AS_TRANSITIONS,
    label: 'Mark Cancelled',
    icon: createElement(IconBan, { size: ICON_SIZE }),
    execute: (context) => {
      selectClaim(context);
      context.setCancelClaimOpen(true);
    },
    disabled: (context) => {
      return !context.canModify;
    },
  },
  [Action.MARK_PENDING_WRITEOFF]: {
    type: Action.MARK_PENDING_WRITEOFF,
    menuGroup: ActionMenuGroup.MARK_AS_TRANSITIONS,
    label: 'Mark Pending Write-Off',
    icon: createElement(IconCurrencyDollarOff, { size: ICON_SIZE }),
    execute: (context) => {
      selectClaim(context);
      context.setMarkPendingWriteOffOpen(true);
    },
    disabled: (context) => {
      return !context.canModify;
    },
  },
  [Action.MARK_UNINSURED]: {
    type: Action.MARK_UNINSURED,
    menuGroup: ActionMenuGroup.MARK_AS_TRANSITIONS,
    label: 'Mark Uninsured',
    icon: createElement(IconShieldOff, { size: ICON_SIZE }),
    execute: (context) => {
      selectClaim(context);
      context.setMarkUninsuredOpen(true);
    },
    disabled: (context) => {
      return !context.canModify;
    },
  },
  [Action.WRITE_OFF]: {
    type: Action.WRITE_OFF,
    menuGroup: ActionMenuGroup.MARK_AS_TRANSITIONS,
    label: 'Write Off',
    icon: createElement(IconTrashX, { size: ICON_SIZE }),
    execute: (context) => {
      selectClaim(context);
      context.setWriteOffOpen(true);
    },
    disabled: (context) => {
      return !context.canModify;
    },
  },
  [Action.MATCH]: {
    type: Action.MATCH,
    label: 'Match',
    icon: createElement(IconLink, { size: ICON_SIZE }),
    execute: (context) => {
      selectClaim(context);
      context.setMatchOpen(true);
    },
    disabled: (context) => {
      return !context.canModify;
    },
  },
  [Action.MARK_HISTORICAL]: {
    type: Action.MARK_HISTORICAL,
    menuGroup: ActionMenuGroup.MARK_AS_TRANSITIONS,
    label: 'Mark Historical',
    icon: createElement(IconHistoryToggle, { size: ICON_SIZE }),
    execute: (context) => {
      selectClaim(context);
      context.setMarkHistoricalOpen(true);
    },
    disabled: (context) => {
      return !context.canModify;
    },
  },
  [Action.MARK_READY]: {
    type: Action.MARK_READY,
    menuGroup: ActionMenuGroup.MARK_AS_TRANSITIONS,
    label: 'Mark Ready',
    icon: createElement(IconCircleCheck, { size: ICON_SIZE }),
    execute: (context) => {
      selectClaim(context);
      context.setMarkReadyOpen(true);
    },
    disabled: (context) => {
      return !context.canModify;
    },
  },
  [Action.VIEW_INVOICE]: {
    type: Action.VIEW_INVOICE,
    label: 'View Invoice',
    icon: createElement(IconReceipt2, { size: ICON_SIZE }),
    execute: (context) => {
      selectClaim(context);
      context.setInvoiceOpen(true);
    },
  },
  [Action.VIEW_RECEIPT]: {
    type: Action.VIEW_RECEIPT,
    label: 'View Receipt',
    icon: createElement(IconReceiptDollar, { size: ICON_SIZE }),
    execute: (context) => {
      selectClaim(context);
      context.setReceiptOpen(true);
    },
  },
  [Action.CREATE_REQUEST]: {
    type: Action.CREATE_REQUEST,
    label: 'Create Request',
    icon: createElement(IconDirections, { size: ICON_SIZE }),
    execute: (context) => {
      if (!isFeatureEnabled('action_management')) {
        return;
      }
      selectClaim(context);
      context.setActionRequestOpen(true);
    },
    disabled: (context) => {
      return !context.canModify || !isFeatureEnabled('action_management');
    },
  },
  [Action.VIEW_REQUESTS]: {
    type: Action.VIEW_REQUESTS,
    label: 'View Requests',
    icon: createElement(IconListCheck, { size: ICON_SIZE }),
    execute: (context) => {
      if (!isFeatureEnabled('action_management')) {
        return;
      }
      selectClaim(context);
      context.setViewActionsOpen?.(true);
    },
    disabled: (context) => {
      return (
        !isFeatureEnabled('action_management') ||
        (context.claim?.actionRequests?.length ?? 0) === 0
      );
    },
  },
  [Action.DUPLICATE]: {
    type: Action.DUPLICATE,
    label: 'Duplicate Claim',
    icon: createElement(IconCopy, { size: ICON_SIZE }),
    execute: (context) => {
      selectClaim(context);
      context.onDuplicateClaim?.(context.claim);
    },
    disabled: (context) => {
      return !context.canModify;
    },
  },
  [Action.VIEW_SOURCE_DATA]: {
    type: Action.VIEW_SOURCE_DATA,
    label: 'View Source Data',
    icon: createElement(IconDatabaseSearch, { size: ICON_SIZE }),
    execute: (context) => {
      selectClaim(context);
      context.setSourceDataOpen?.(true);
    },
    disabled: (context) => {
      const messageID = context.claim?.messageID;
      if (typeof messageID !== 'string') {
        return true;
      }
      return messageID.trim().length === 0;
    },
  },
  [Action.VIEW_ATTACHMENTS]: {
    type: Action.VIEW_ATTACHMENTS,
    label: 'View Attachments',
    icon: createElement(IconPaperclip, { size: ICON_SIZE }),
    execute: (context) => {
      selectClaim(context);
      context.setAttachmentsOpen?.(true);
    },
  },
  [Action.UPLOAD_SUPPORTING_DOCUMENT]: {
    type: Action.UPLOAD_SUPPORTING_DOCUMENT,
    label: 'Upload To MB Health',
    icon: createElement(IconFileUpload, { size: ICON_SIZE }),
    execute: (context) => {
      selectClaim(context);
      context.setSupportingDocumentOpen?.(true);
    },
    disabled: (context) =>
      !context.canModify ||
      !context.canCreateQuery ||
      !hasSCIClaimQueryData(context.claim),
  },
};

export const partitionActionsForMenu = (
  actions: ActionMetadata[]
): ActionMenuEntry[] => {
  const groupedActionsByGroup = new Map<ActionMenuGroup, ActionMetadata[]>();
  const firstIndexByGroup = new Map<ActionMenuGroup, number>();

  actions.forEach((action, index) => {
    const group = getActionMenuGroup(action);
    if (group === ActionMenuGroup.DEFAULT) {
      return;
    }

    if (!groupedActionsByGroup.has(group)) {
      groupedActionsByGroup.set(group, []);
      firstIndexByGroup.set(group, index);
    }

    groupedActionsByGroup.get(group)?.push(action);
  });

  const menuEntries: ActionMenuEntry[] = [];

  actions.forEach((action, index) => {
    const group = getActionMenuGroup(action);
    if (group === ActionMenuGroup.DEFAULT) {
      menuEntries.push({ kind: 'action', action });
      return;
    }

    if (firstIndexByGroup.get(group) !== index) {
      return;
    }

    const groupedActions = groupedActionsByGroup.get(group);
    if (!groupedActions || groupedActions.length === 0) {
      return;
    }

    menuEntries.push({ kind: 'group', group, actions: groupedActions });
  });

  return menuEntries;
};

const pendingBlockedClaimActions = new Set<Action>([
  Action.RESUBMIT,
  Action.INVOICE,
  Action.RECONCILE,
  Action.MARK_INCOMPLETE,
  Action.MARK_PAID,
  Action.MARK_CANCELLED,
  Action.MARK_PENDING_WRITEOFF,
  Action.MARK_UNINSURED,
  Action.MARK_HISTORICAL,
  Action.MARK_READY,
  Action.WRITE_OFF,
  Action.MATCH,
]);

export const determineActions = (
  claim: Claim,
  isProvider: boolean,
  isAdmin: boolean,
  translate: ActionLabelTranslator = (_, defaultValue) => defaultValue ?? '',
  canViewQueries = false
): ActionMetadata[] => {
  const localize = (action: ActionMetadata): ActionMetadata => ({
    ...action,
    label: translate(`pages:claims.actions.${action.type}`, action.label),
    disabled: (context) =>
      (claimHasTariffStatus(context.claim, CLAIM_TARIFF_STATUS.PENDING) &&
        pendingBlockedClaimActions.has(action.type)) ||
      (action.disabled?.(context) ?? false),
  });

  const actionOrder = isProvider
    ? PROVIDER_ACTION_ORDER
    : isAdmin
    ? ADMIN_ACTION_ORDER
    : ACTION_ORDER;

  return actionOrder
    .filter((action) => canDisplayAction(action, claim, canViewQueries))
    .map((action) => localize(baseActionMetadata[action]));
};
